/* ==========================================================================
   Wonder Jacket — product gallery thumbnails

   Progressive enhancement only. The gallery track is a native scroll-snap
   carousel, so swipe, trackpad, arrow keys and the scrollbar all work with
   this file absent. What it adds:

   - clicking a thumbnail scrolls the track to that slide
   - scrolling the track marks the matching thumbnail active
   - the active thumbnail is kept inside the visible strip

   Above 990px the section renders the Figma mosaic instead and the
   thumbnails are display:none, so every handler here becomes inert rather
   than needing a breakpoint check of its own.
   ========================================================================== */

(function () {
  'use strict';

  function initGallery(root) {
    var track = root.querySelector('[data-wj-pdp-track]');
    var thumbWrap = root.querySelector('[data-wj-pdp-thumbs]');
    if (!track || !thumbWrap) return;

    var slides = Array.prototype.slice.call(track.querySelectorAll('[data-wj-pdp-slide]'));
    var thumbs = Array.prototype.slice.call(thumbWrap.querySelectorAll('[data-wj-pdp-thumb]'));
    if (!slides.length || !thumbs.length) return;

    var active = -1;

    function setActive(index) {
      if (index === active || index < 0 || index >= thumbs.length) return;
      active = index;

      thumbs.forEach(function (thumb, i) {
        var on = i === index;
        thumb.classList.toggle('is-active', on);
        if (on) {
          thumb.setAttribute('aria-current', 'true');
        } else {
          thumb.removeAttribute('aria-current');
        }
      });

      // Keep the active thumbnail in view without scrolling the page itself.
      var thumb = thumbs[index];
      var left = thumb.offsetLeft;
      var right = left + thumb.offsetWidth;
      if (left < thumbWrap.scrollLeft) {
        thumbWrap.scrollLeft = left - 12;
      } else if (right > thumbWrap.scrollLeft + thumbWrap.clientWidth) {
        thumbWrap.scrollLeft = right - thumbWrap.clientWidth + 12;
      }
    }

    function scrollToSlide(index) {
      var slide = slides[index];
      if (!slide) return;
      // scrollIntoView would also scroll the page vertically on some
      // browsers, so move the track's own scroll position instead.
      track.scrollTo({
        left: slide.offsetLeft - track.offsetLeft,
        behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
      });
      setActive(index);
    }

    thumbs.forEach(function (thumb, index) {
      thumb.addEventListener('click', function () {
        scrollToSlide(index);
      });
    });

    var prevBtn = root.querySelector('[data-wj-pdp-prev]');
    var nextBtn = root.querySelector('[data-wj-pdp-next]');
    if (prevBtn) {
      prevBtn.addEventListener('click', function () {
        scrollToSlide(Math.max(0, active - 1));
      });
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', function () {
        scrollToSlide(Math.min(slides.length - 1, active + 1));
      });
    }

    // Fired by the variant-price script (on document, since it lives outside
    // this gallery's own root) when the selected variant has its own
    // assigned image, so picking a swatch jumps the gallery to it.
    document.addEventListener('wj:pdp-select-image', function (event) {
      var imageId = event.detail && String(event.detail.imageId);
      if (!imageId) return;
      var index = slides.findIndex(function (slide) {
        return slide.dataset.wjPdpImageId === imageId;
      });
      if (index !== -1) scrollToSlide(index);
    });

    /* --------------------------------------------------------------------
       Track position -> active thumbnail.

       IntersectionObserver against the track reports whichever slide is most
       visible, which is what "current" means for a snap carousel.
       -------------------------------------------------------------------- */

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          var best = null;
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            if (!best || entry.intersectionRatio > best.intersectionRatio) best = entry;
          });
          if (!best) return;
          setActive(parseInt(best.target.getAttribute('data-wj-pdp-slide'), 10));
        },
        { root: track, threshold: [0.5, 0.75, 1] }
      );

      slides.forEach(function (slide) {
        observer.observe(slide);
      });
    } else {
      // Older browsers: derive the index from the scroll offset.
      var ticking = false;
      track.addEventListener(
        'scroll',
        function () {
          if (ticking) return;
          ticking = true;
          window.requestAnimationFrame(function () {
            var width = track.clientWidth || 1;
            setActive(Math.round(track.scrollLeft / width));
            ticking = false;
          });
        },
        { passive: true }
      );
    }

    setActive(0);
  }

  function init() {
    document.querySelectorAll('[data-wj-pdp-gallery]').forEach(initGallery);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // The theme editor swaps section markup without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    event.target.querySelectorAll('[data-wj-pdp-gallery]').forEach(initGallery);
  });
})();
